import { useCallback, useMemo, useState } from 'react';
import { AlertTriangle, CalendarClock, EyeOff, HelpCircle } from 'lucide-react';
import { NoteDisclosure } from '@/components/Disclosure';
import { Hint } from '@/components/Hint';
import { StatusPill } from '@/components/Status';
import { statusPillClass } from '@/lib/status';
import type { ReleaseRow } from '@/api/orders';
import { useOrdersDataStore } from '@/stores/ordersDataStore';
import { dueTone, fmtDate, fmtDateLong } from './lib';
import { JourneyName, WorkEmpty, WorkLoading, WorkNotice, WorkRow, WorkSection } from './workLib';
import { todayDateString } from './workUtils';
import { ProductLink } from './ProductLink';
import { daysUntil, releaseBand, releaseWaitingOn, type ReleaseBand as Band } from './releasesLib';

/**
 * The Make-live queue.
 *
 * A release has a SELL EMBARGO (street date) — the first day its products may
 * appear on the shop — and its products sit in the catalog with hide/
 * hide_in_shop set until someone lifts them. Nothing joined those two facts
 * before: the embargo lived in the setup workspace's NOTES.md headers and the
 * hidden flags lived in acot-db, so "which releases are due and still dark?"
 * had no answer short of checking each one by hand.
 *
 * This view is read-only by design. It says what is due, what is still hidden,
 * and links each hidden product into the backend — the unhiding itself happens
 * there, where the rest of the catalog editing happens.
 *
 * The embargo, not the ship date, orders this list. The two genuinely differ:
 * Ranger ships about ten days BEFORE its launch day, Uniquely Creative ships by
 * the 25th against a sell embargo on the 1st. Sorting by arrival would put
 * releases in the wrong order and, worse, invite making one live early.
 */

/** "in 3 days" / "today" / "5 days ago" — the countdown that makes the list scan. */
function relativeDay(date: string): string {
  const d = daysUntil(date);
  if (d === 0) return 'today';
  if (d === 1) return 'tomorrow';
  if (d === -1) return 'yesterday';
  return d > 0 ? `in ${d} days` : `${-d} days ago`;
}

const BAND_TITLES: Record<Band, string> = {
  early: 'Live before embargo',
  overdue: 'Street date passed',
  soon: 'Next 7 days',
  upcoming: 'Upcoming',
  undated: 'No street date',
  live: 'Live',
};

function ReleaseRowItem({ release, expanded, onToggle, onOpenJourney }: {
  release: ReleaseRow;
  expanded: boolean;
  onToggle: () => void;
  onOpenJourney: () => void;
}) {
  const v = release.visibility;
  const embargo = release.embargoDate;
  const past = embargo != null && embargo <= todayDateString();
  const waiting = releaseWaitingOn(release);
  const canExpand = (v?.hiddenProducts.length ?? 0) > 0 || release.releaseNames.length > 0;

  const detail = expanded && (
    <div className="space-y-2">
      {/* The categories this release resolved to — the bridge between the
          journey and the catalog, shown so a wrong stamp is visible. */}
      {release.releaseNames.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {release.releaseNames.map((n, i) => (
            <StatusPill
              key={`${n.catId ?? 'x'}-${i}`}
              tone={n.category?.resolved ? 'neutral' : 'warning'}
            >
              {n.name ?? `cat ${n.catId}`}
              {n.category?.resolved && (
                <span className="ml-1 tabular-nums">
                  {n.category.hidden}/{n.category.products} hidden
                </span>
              )}
              {n.catId == null && <span className="ml-1">not stamped</span>}
            </StatusPill>
          ))}
        </div>
      )}

      {v && v.hiddenProducts.length > 0 && (
        <div>
          <div className="mb-1 text-xs text-muted-foreground">
            Hidden products{v.hiddenTruncated && ' (first 250 shown)'}
          </div>
          <div className="grid gap-x-4 gap-y-0.5 md:grid-cols-2">
            {v.hiddenProducts.map((h) => (
              <ProductLink key={h.pid} pid={h.pid} className="truncate text-xs text-muted-foreground">
                {[h.itemnumber, h.description].filter(Boolean).join(' · ') || `pid ${h.pid}`}
                {h.available <= 0 && ' · no stock'}
              </ProductLink>
            ))}
          </div>
        </div>
      )}

      {release.setupFolders.length > 0 && (
        <div className="text-xs text-muted-foreground">
          Setup {release.setupFolders.length === 1 ? 'folder' : 'folders'}:{' '}
          {release.setupFolders.map((f) => `${f.folder} (${f.status ?? 'unknown'})`).join(', ')}
        </div>
      )}

      <NoteDisclosure text={release.notes} />
    </div>
  );

  return (
    <WorkRow
      scrollKey={`release:${release.id}`}
      title={<JourneyName vendor={release.vendor} label={release.label} onOpen={onOpenJourney} />}
      expanded={expanded}
      onToggle={canExpand ? onToggle : undefined}
      // Keeps this row's title aligned with its expandable neighbours.
      lead={canExpand ? undefined : <span />}
      detail={detail || undefined}
    >
      {/* The street date, and how far off it is, toned as a deadline: past
          and not fully live is danger (a lifted embargo on a still-dark
          release is the whole point of the view), this week is warning. Once
          the release is live a passed date is done, so it goes neutral. */}
      {embargo ? (
        <Hint content={`Sell embargo — the first day these products may go live${
          release.expectedAvailability ? `. Vendor ships ${fmtDateLong(release.expectedAvailability)}` : ''
        }`}>
          <span className={statusPillClass(past && release.state === 'live' ? 'neutral' : (dueTone(embargo) ?? 'neutral'))}>
            <CalendarClock className="h-3 w-3" />
            {fmtDate(embargo)} · {relativeDay(embargo)}
          </span>
        </Hint>
      ) : (
        <Hint content="No sell embargo recorded on this journey.">
          <span className={statusPillClass('warning')}>
            <HelpCircle className="h-3 w-3" />
            no street date
          </span>
        </Hint>
      )}

      {/* Live shop status. Null visibility never renders as a number — and
          neither does an EMPTY one: a resolved category holding no products
          returned {products: 0, hidden: 0}, which rendered as "all 0 live"
          and read as finished on releases that had not been built at all. */}
      {v && v.products > 0 ? (
        v.toMakeLive > 0 ? (
          <Hint content={`${v.toMakeLive} of ${v.products} products in this release are still hidden on the shop`}>
            <span className={statusPillClass('warning')}>
              <EyeOff className="h-3 w-3" />
              {v.toMakeLive} of {v.products} hidden
            </span>
          </Hint>
        ) : v.stragglers > 0 ? (
          <Hint content={`${v.stragglers} hidden with no stock — not counted`}>
            <span>
              <StatusPill tone="success">{v.live} of {v.products} live</StatusPill>
            </span>
          </Hint>
        ) : (
          <StatusPill tone="success">all {v.products} live</StatusPill>
        )
      ) : waiting ? (
        // Carries the urgency of its band: a release still unbuilt after its
        // street date is the loudest thing on the page, not a grey footnote.
        <Hint content={waiting.hint}>
          <span className={statusPillClass(past ? 'danger' : 'warning')}>
            <AlertTriangle className="h-3 w-3" />
            {waiting.label}
          </span>
        </Hint>
      ) : null}

    </WorkRow>
  );
}

export function ReleasesView({ onOpenJourney }: { onOpenJourney: (orderId: string) => void }) {
  // Read by the data store beside the overview, so the rows here and the
  // count on the nav rail come from one response.
  const data = useOrdersDataStore((s) => s.releases);
  const loadError = useOrdersDataStore((s) => s.releasesError);
  const loading = useOrdersDataStore((s) => s.loading);
  const load = useOrdersDataStore((s) => s.load);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const bands = useMemo(() => {
    const out: Record<Band, ReleaseRow[]> = {
      early: [], overdue: [], soon: [], upcoming: [], undated: [], live: [] };
    for (const r of data?.releases ?? []) out[releaseBand(r)].push(r);
    for (const key of Object.keys(out) as Band[]) {
      // The street date orders every dated band. The undated band has none to
      // sort on, so it falls back to expected arrival — goods landing soonest
      // are the ones whose missing street date matters first.
      out[key].sort(key === 'undated'
        ? (a, b) => (a.expectedAvailability || '9999').localeCompare(b.expectedAvailability || '9999')
          || Number(b.id) - Number(a.id)
        : (a, b) => (a.embargoDate || '9999').localeCompare(b.embargoDate || '9999')
          || Number(b.id) - Number(a.id));
    }
    return out;
  }, [data]);

  if (loading && !data) {
    return <WorkLoading>Loading releases…</WorkLoading>;
  }

  if (loadError && !data) {
    return <WorkNotice tone="danger" onRetry={() => void load()}>{loadError}</WorkNotice>;
  }

  const order: Band[] = ['early', 'overdue', 'soon', 'upcoming', 'undated', 'live'];
  const anyRows = (data?.releases.length ?? 0) > 0;

  return (
    <div>
      {/* A failed catalog read is stated, never absorbed: without this banner
          every row would read "no data" and look indistinguishable from a
          release with nothing left to do. */}
      {data?.visibilityError && (
        <WorkNotice tone="danger" onRetry={() => void load()}>
          Live shop status could not be read ({data.visibilityError}). Dates below are correct;
          every release's hidden count is unknown, not zero.
        </WorkNotice>
      )}

      {!anyRows && <WorkEmpty>No releases in the window.</WorkEmpty>}

      {order.map((band) => bands[band].length > 0 && (
        <WorkSection key={band} title={BAND_TITLES[band]} count={bands[band].length}>
          {bands[band].map((r) => (
            <ReleaseRowItem
              key={r.id}
              release={r}
              expanded={expanded.has(r.id)}
              onToggle={() => toggle(r.id)}
              onOpenJourney={() => onOpenJourney(r.id)}
            />
          ))}
        </WorkSection>
      ))}
    </div>
  );
}
